import { CommitmentSuccess } from "@mod/commitment/ui/form/commitment-success";
import { LoadingSpinner } from "@mod/core/ui";
import { createBrowserRouter } from "react-router-dom";
import { MainLayout } from "./layout/main-layout";
import { ProyectedLayout } from "./layout/protected-layout";
import { ErrorPage } from "./pages/error";

export const APP_ROUTES = {
  HOME: "/",
  LOGIN: "/login",
  SIGNIN_SERVER: "/signin-server",
  RESET_PASSWORD: "/reset-password",
  GROUP: "/group/:id",
  GROUPS: "/groups",
  GROUP_CONFIG: "/group/:id/config",
  NEW_GROUP: "/group/new",
  EVENT_ROOMS: "/group/:id/event/:eventId/rooms",
  EVENT_WORKSHOPS: "/group/:id/event/:eventId/workshops",
  EVENT_DASHBOARD: "/group/:id/event/:eventId/dashboard",
  EVENTS: "/events",
  EVENT: "/group/:id/event/:eventId",
  EVENT_CONFIG: "/group/:id/event/:eventId/config",
  EVENT_PAYMENTS: "/group/:id/event/:eventId/payments",
  EVENT_GUEST_CONFIG: "/group/:id/event/:eventId/guest/:guestId",
  NEW_EVENT: "/group/:id/event-new",
  FORM: "/form/:eventId",
  GUEST_PAYMENT: "/guest-payment/:eventId/:guestId",
  SECURITY: "/security",
  SERVICES: "/services",
  INVITATIONS: "/invitations",
  INVITATION_BY_CODE: "/invitation/:code",
  INVITATION_BY_CODE_AND_HASH: "/invitation/:code/:hash",
  EVENT_COMISSIONS: "/group/:id/event/:eventId/comissions",
  EVENT_ATTENDANCE: "/group/:id/event/:eventId/attendance",
  COMMITMENT: "/commitment",
  COMMITMENT_INSCRIPTION: "/commitment/:commitmentHash/:groupCode",
  COMMITMENT_GUEST_INSCRIPTION: "/commitment-guest/:commitmentHash",
  COMMITMENT_STATUS_BY_GROUP: "/commitment/:commitmentHash/:groupCode/status",
  PRAYER_REQUESTS: "/prayer-requests",
  PRAYER_REQUEST_NEW: "/prayer-requests/new",
  PRAYER_REQUEST_EDIT: "/prayer-requests/:id/edit",
  PRAYER_REQUEST_DETAILS: "/prayer-requests/:id/details",
  PRAYER_REQUEST_PUBLIC: "/prayer-request/:code",
  PREACHING: "/preaching",
  LANDING_CONFIG: "/landing-config",
  PROFILE: "/profile",
  MEETING_CHECKIN: "/attendance/check-in",
  MEETING_CHECKIN_SHORT: "/a/:code"
};

const appRoutes = [
  {
    path: APP_ROUTES.LOGIN,
    errorElement: <ErrorPage />,
    async lazy() {
      const { LoginPage } = await import("./pages/login");
      return { Component: LoginPage };
    }
  },
  {
    path: APP_ROUTES.SIGNIN_SERVER,
    errorElement: <ErrorPage />,
    async lazy() {
      const { SignInServerPage } = await import("./pages/signin-server");
      return { Component: SignInServerPage };
    }
  },
  {
    path: APP_ROUTES.RESET_PASSWORD,
    errorElement: <ErrorPage />,
    async lazy() {
      const { ResetPasswordPage } = await import("./pages/reset-password");
      return { Component: ResetPasswordPage };
    }
  },
  {
    path: APP_ROUTES.HOME,
    element: <ProyectedLayout />,
    loader: async () => <LoadingSpinner />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: APP_ROUTES.HOME,
        loader: async () => <LoadingSpinner />,
        async lazy() {
          const { HomePage } = await import("./pages/home");
          return { Component: HomePage };
        }
      },
      {
        path: APP_ROUTES.EVENTS,
        loader: async () => <LoadingSpinner />,
        async lazy() {
          const { EventsPage } = await import("./pages/events/event");
          return { Component: EventsPage };
        }
      },
      {
        path: APP_ROUTES.EVENT_ROOMS,
        async lazy() {
          const { EventRooms } = await import("./pages/events/event-rooms");
          return { Component: EventRooms };
        }
      },
      {
        path: APP_ROUTES.EVENT_WORKSHOPS,
        async lazy() {
          const { EventWorkshopsPage } = await import("./pages/events/event-workshops");
          return { Component: EventWorkshopsPage };
        }
      },
      {
        path: APP_ROUTES.EVENT_DASHBOARD,
        async lazy() {
          const { EventDashboard } = await import("./pages/events/event-dashboard");
          return { Component: EventDashboard };
        }
      },
      {
        path: APP_ROUTES.GROUPS,
        async lazy() {
          const { GroupsPage } = await import("./pages/groups");
          return { Component: GroupsPage };
        }
      },
      {
        path: APP_ROUTES.GROUP,
        async lazy() {
          const { GroupPage } = await import("./pages/group");
          return { Component: GroupPage };
        }
      },
      {
        path: APP_ROUTES.NEW_GROUP,
        async lazy() {
          const { NewGroupPage } = await import("./pages/new-group");
          return { Component: NewGroupPage };
        }
      },
      {
        path: APP_ROUTES.GROUP_CONFIG,
        async lazy() {
          const { GroupConfigPage } = await import("./pages/group-config");
          return { Component: GroupConfigPage };
        }
      },
      {
        path: APP_ROUTES.NEW_EVENT,
        loader: async () => <LoadingSpinner />,
        async lazy() {
          const { NewEventPage } = await import("./pages/events/new-event");
          return { Component: NewEventPage };
        }
      },
      {
        path: APP_ROUTES.EVENT_GUEST_CONFIG,
        loader: async () => <LoadingSpinner />,
        async lazy() {
          const { EventGuestPage } = await import("./pages/events/event-guest");
          return { Component: EventGuestPage };
        }
      },
      {
        path: APP_ROUTES.EVENT_CONFIG,
        loader: async () => <LoadingSpinner />,
        async lazy() {
          const { EventConfigPage } = await import("./pages/events/event-config");
          return { Component: EventConfigPage };
        }
      },
      {
        path: APP_ROUTES.EVENT_PAYMENTS,
        loader: async () => <LoadingSpinner />,
        async lazy() {
          const { EventPaymentsPage } = await import("./pages/events/event-payments");
          return { Component: EventPaymentsPage };
        }
      },
      {
        path: APP_ROUTES.EVENT_COMISSIONS,
        loader: async () => <LoadingSpinner />,
        async lazy() {
          const { EventComissionsPage } = await import("./pages/events/event-comissions");
          return { Component: EventComissionsPage };
        }
      },
      {
        path: APP_ROUTES.SECURITY,
        async lazy() {
          const { SecurityPage } = await import("./pages/security");
          return { Component: SecurityPage };
        }
      },
      {
        path: APP_ROUTES.SERVICES,
        async lazy() {
          const { ServicesPage } = await import("./pages/services/services");
          return { Component: ServicesPage };
        }
      },
      {
        path: APP_ROUTES.INVITATIONS,
        async lazy() {
          const { InvitationsPage } = await import("./pages/invitations/invitations");
          return { Component: InvitationsPage };
        }
      },
      {
        path: APP_ROUTES.EVENT_ATTENDANCE,
        async lazy() {
          const { EventAttendancePage } = await import("./pages/events/event-attendance");
          return { Component: EventAttendancePage };
        }
      },
      {
        path: APP_ROUTES.COMMITMENT,
        async lazy() {
          const { CommitmentPage } = await import("./pages/commitment/commitment-home");
          return { Component: CommitmentPage };
        }
      },
      {
        path: APP_ROUTES.PRAYER_REQUESTS,
        async lazy() {
          const { PrayerRequestsPage } = await import("./pages/prayer-requests/prayer-requests");
          return { Component: PrayerRequestsPage };
        }
      },
      {
        path: APP_ROUTES.PRAYER_REQUEST_NEW,
        async lazy() {
          const { PrayerRequestFormPage } = await import("./pages/prayer-requests/prayer-request-form");
          return { Component: PrayerRequestFormPage };
        }
      },
      {
        path: APP_ROUTES.PRAYER_REQUEST_EDIT,
        async lazy() {
          const { PrayerRequestFormPage } = await import("./pages/prayer-requests/prayer-request-form");
          return { Component: PrayerRequestFormPage };
        }
      },
      {
        path: APP_ROUTES.PRAYER_REQUEST_DETAILS,
        async lazy() {
          const { PrayerRequestDetailsPage } = await import("./pages/prayer-requests/prayer-request-details");
          return { Component: PrayerRequestDetailsPage };
        }
      },
      {
        path: APP_ROUTES.PREACHING,
        async lazy() {
          const { PreachingPage } = await import("./pages/preaching/preaching");
          return { Component: PreachingPage };
        }
      },
      {
        path: APP_ROUTES.LANDING_CONFIG,
        async lazy() {
          const { LandingConfigPage } = await import("./pages/landing-config");
          return { Component: LandingConfigPage };
        }
      },
      {
        path: APP_ROUTES.PROFILE,
        async lazy() {
          const { ProfilePage } = await import("./pages/profile");
          return { Component: ProfilePage };
        }
      }
    ]
  },
  {
    path: APP_ROUTES.FORM,
    element: <MainLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: APP_ROUTES.FORM,
        async lazy() {
          const { FormPage } = await import("./pages/form");
          return { Component: FormPage };
        }
      }
    ]
  },
  {
    path: APP_ROUTES.GUEST_PAYMENT,
    element: <MainLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: APP_ROUTES.GUEST_PAYMENT,
        async lazy() {
          const { GuestPaymentPage } = await import("./pages/events/guest-payment");
          return { Component: GuestPaymentPage };
        }
      }
    ]
  },
  {
    path: APP_ROUTES.INVITATION_BY_CODE,
    errorElement: <ErrorPage />,
    children: [
      {
        path: APP_ROUTES.INVITATION_BY_CODE,
        async lazy() {
          const { InvitationByCodePage } = await import("./pages/invitations/invitation");
          return { Component: InvitationByCodePage };
        }
      }
    ]
  },
  {
    path: APP_ROUTES.INVITATION_BY_CODE_AND_HASH,
    errorElement: <ErrorPage />,
    children: [
      {
        path: APP_ROUTES.INVITATION_BY_CODE_AND_HASH,
        async lazy() {
          const { InvitationByCodePage } = await import("./pages/invitations/invitation");
          return { Component: InvitationByCodePage };
        }
      }
    ]
  },
  {
    path: APP_ROUTES.COMMITMENT_INSCRIPTION,
    errorElement: <CommitmentSuccess />,
    children: [
      {
        path: APP_ROUTES.COMMITMENT_INSCRIPTION,
        async lazy() {
          const { CommitmentFormPage } = await import("./pages/commitment/commitment-form");
          return { Component: CommitmentFormPage };
        }
      }
    ]
  },
  {
    path: APP_ROUTES.COMMITMENT_GUEST_INSCRIPTION,
    errorElement: <CommitmentSuccess />,
    children: [
      {
        path: APP_ROUTES.COMMITMENT_GUEST_INSCRIPTION,
        async lazy() {
          const { CommitmentGuestFormPage } = await import("./pages/commitment/commitment-guest-form");
          return { Component: CommitmentGuestFormPage };
        }
      }
    ]
  },
  // {
  //   path: APP_ROUTES.COMMITMENT_STATUS_BY_GROUP,
  //   errorElement: <ErrorPage />,
  //   children: [
  //     {
  //       path: APP_ROUTES.COMMITMENT_STATUS_BY_GROUP,
  //       async lazy() {
  //         const { CommitmentStatusByGroupPage } = await import("./pages/commitment/commitment-group-status");
  //         return { Component: CommitmentStatusByGroupPage };
  //       }
  //     }
  //   ]
  // },
  {
    path: APP_ROUTES.PRAYER_REQUEST_PUBLIC,
    errorElement: <ErrorPage />,
    children: [
      {
        path: APP_ROUTES.PRAYER_REQUEST_PUBLIC,
        async lazy() {
          const { PrayerRequestPublicPage } = await import("./pages/prayer-requests/prayer-request-public");
          return { Component: PrayerRequestPublicPage };
        }
      }
    ]
  },
  {
    path: APP_ROUTES.MEETING_CHECKIN,
    errorElement: <ErrorPage />,
    children: [
      {
        path: APP_ROUTES.MEETING_CHECKIN,
        async lazy() {
          const { GroupMeetingCheckInPage } = await import("./pages/group-meeting-checkin-page");
          return { Component: GroupMeetingCheckInPage };
        }
      }
    ]
  },
  {
    path: APP_ROUTES.MEETING_CHECKIN_SHORT,
    errorElement: <ErrorPage />,
    children: [
      {
        path: APP_ROUTES.MEETING_CHECKIN_SHORT,
        async lazy() {
          const { GroupMeetingCheckInPage } = await import("./pages/group-meeting-checkin-page");
          return { Component: GroupMeetingCheckInPage };
        }
      }
    ]
  }
];

let _appRouter: ReturnType<typeof createBrowserRouter> | undefined;

// Supabase falls back to the project "Site URL" (e.g. "/") when the requested redirectTo is not
// allow-listed. Move any recovery link to the reset page before the protected layout redirects to
// /login and the token in the hash is lost.
const forwardRecoveryLinkToResetPage = () => {
  const { hash, pathname } = window.location;
  const isRecoveryHash = /(^#|&)type=recovery(&|$)/.test(hash) || /(^#|&)error_code=otp_expired(&|$)/.test(hash);

  if (isRecoveryHash && pathname !== APP_ROUTES.RESET_PASSWORD) {
    window.history.replaceState(null, "", `${APP_ROUTES.RESET_PASSWORD}${hash}`);
  }
};

// Lazily create the router so importing this module (e.g. for APP_ROUTES in tests)
// does not instantiate createBrowserRouter, which triggers navigation/fetch side effects.
export const getAppRouter = () => {
  if (!_appRouter) {
    forwardRecoveryLinkToResetPage();
    _appRouter = createBrowserRouter(appRoutes);
  }
  return _appRouter;
};
